<template>
  <v-container>
    <!-- TICKET-2778: the Treasurer's view of the Clerk <-> Treasurer link. Read-only:
         what was sent to the clerk and what the clerk did with it, and the Clerk
         batches imported here. Nothing on this page changes either office's data. -->
    <v-row class="mt-4" align="center">
      <v-col cols="12" md="6">
        <h3>Clerk System</h3>
        <p class="mb-0 text--secondary">
          Files sent to the Clerk system wait in the clerk's inbox until the clerk imports them.
        </p>
      </v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn
          id="tmc-clerk-system-test-connection"
          class="mr-2"
          outlined
          :loading="testing"
          @click="testConnection">
          Test Clerk connection
        </v-btn>
        <v-btn
          id="tmc-clerk-system-refresh"
          :loading="loading"
          @click="loadStatus">
          <v-icon left>{{ icons.mdiReload }}</v-icon> Refresh
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="connection" :type="connection.ok ? 'success' : 'error'" dense text>
      {{ connectionText }}
    </v-alert>
    <v-alert v-if="!loading && status.configured === false" type="info" dense text>
      The Clerk system link is not set up for this county. Files go to the clerk over SFTP as before.
    </v-alert>
    <v-alert v-if="loadError" type="error" dense text>{{ loadError }}</v-alert>

    <template v-if="status.configured">
      <h4 class="mt-4">Sent to the Clerk system</h4>
      <v-alert v-if="status.sentError" type="warning" dense text>
        Unable to read the Clerk system's inbox: {{ status.sentError }}
      </v-alert>
      <v-data-table
        id="tmc-clerk-system-sent"
        dense
        :headers="sentHeaders"
        :items="status.sent || []"
        :loading="loading"
        item-key="id"
        no-data-text="Nothing has been sent to the Clerk system yet."
        class="elevation-1">
        <template v-slot:[`item.kind`]="{ item }">
          {{ kindText(item.kind) }}
        </template>
        <template v-slot:[`item.sentAt`]="{ item }">
          {{ dateTime(item.sentAt) }}<span v-if="item.sentBy"> by {{ item.sentBy }}</span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip small label :color="statusColor(item.status)" text-color="white">
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:[`item.processed`]="{ item }">
          <span v-if="item.processedAt">
            {{ dateTime(item.processedAt) }}<span v-if="item.processedBy"> by {{ item.processedBy }}</span>
          </span>
        </template>
        <template v-slot:[`item.outcome`]="{ item }">
          <span v-if="item.status === 'rejected'" class="error--text">{{ item.rejectReason || 'Rejected' }}</span>
          <span v-else>{{ resultText(item.result) }}</span>
        </template>
      </v-data-table>

      <h4 class="mt-6">Received from the Clerk system</h4>
      <v-data-table
        id="tmc-clerk-system-received"
        dense
        :headers="receivedHeaders"
        :items="status.received || []"
        :loading="loading"
        item-key="batchId"
        no-data-text="No Clerk batches have been imported here yet."
        class="elevation-1">
        <template v-slot:[`item.importedAt`]="{ item }">
          {{ dateTime(item.importedAt) }}<span v-if="item.importedBy"> by {{ item.importedBy }}</span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span :class="item.status === 'acked' ? '' : 'warning--text'">
            {{ receivedText(item.status) }}
          </span>
          <div v-if="item.status !== 'acked' && item.lastAckError" class="text-caption">
            {{ item.lastAckError }}
          </div>
        </template>
      </v-data-table>
    </template>
  </v-container>
</template>

<script>
import { mdiReload } from '@mdi/js';
import { clients } from '../../util/clients';
import { INBOX_STATUS_COLORS, RECEIVED_STATUS_TEXT } from '../../util/shared/treasurer-link';

const { backendRest } = clients.direct;

export default {
  name: 'ClerkSystemStatus',
  data: () => ({
    icons: { mdiReload },
    loading: false,
    testing: false,
    loadError: '',
    connection: undefined,
    status: {},
    sentHeaders: [
      { text: 'File', value: 'fileName' },
      { text: 'Kind', value: 'kind' },
      { text: 'Rows', value: 'rowCount', align: 'end' },
      { text: 'Sent', value: 'sentAt' },
      { text: 'Status', value: 'status' },
      { text: 'Clerk action', value: 'processed', sortable: false },
      { text: 'Outcome', value: 'outcome', sortable: false },
    ],
    receivedHeaders: [
      { text: 'File', value: 'fileName' },
      { text: 'Rows imported', value: 'rowsImported', align: 'end' },
      { text: 'Imported', value: 'importedAt' },
      { text: 'Status', value: 'status' },
    ],
  }),
  computed: {
    connectionText() {
      const c = this.connection || {};
      if (c.ok) {
        return `Connected to the Clerk system${c.tenantName ? ` (${c.tenantName})` : ''}.`;
      }
      return c.message || 'Unable to reach the Clerk system.';
    },
  },
  created() {
    this.loadStatus();
  },
  methods: {
    async loadStatus() {
      this.loading = true;
      this.loadError = '';
      try {
        const { data } = await backendRest.get('/treasurer-link/status');
        this.status = data || {};
      } catch (err) {
        console.error(err);
        this.loadError = ((err && err.response && err.response.data) || {}).error || 'Unable to read the Clerk system status.';
      } finally {
        this.loading = false;
      }
    },
    async testConnection() {
      this.testing = true;
      try {
        const { data } = await backendRest.get('/treasurer-link/ping');
        this.connection = data || { ok: false };
      } catch (err) {
        console.error(err);
        this.connection = { ok: false, message: 'Unable to reach the server.' };
      } finally {
        this.testing = false;
      }
    },
    kindText(kind) {
      if (kind === 'paidWarrants') return 'Paid warrants';
      if (kind === 'balance') return 'Balances';
      return kind;
    },
    statusColor(status) {
      return INBOX_STATUS_COLORS[status] || 'grey';
    },
    receivedText(status) {
      return RECEIVED_STATUS_TEXT[status] || status;
    },
    resultText(result) {
      if (!result) return '';
      const parts = [];
      if (result.updated != null) parts.push(`${result.updated} updated`);
      // the contract does not pin whether these are counts or row lists; show a count either way
      const count = (v) => (Array.isArray(v) ? v.length : Number(v) || 0);
      if (count(result.alreadyPaid)) parts.push(`${count(result.alreadyPaid)} already paid`);
      if (count(result.unmatched)) parts.push(`${count(result.unmatched)} not found`);
      return parts.join(', ');
    },
    dateTime(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
  },
};
</script>
